import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { TextField, Card } from "@mui/material";
import { memberHandler } from "../../redux/handlers";
import { createTableColumns } from "../../utils/createTableColumns";
import { useGlobalTable } from "../../hooks/useGlobalTable";
import AllderTable from "../../components/AllderTable";

const TEXTFIELD_STYLES = {
  InputProps: { sx: { borderRadius: "7px" } },
  sx: {
    ".Mui-disabled": {
      background: "#F8F8F9",
    },
  },
  className: "w-[200px] !ml",
  size: "small",
};

export function Customer() {
  const { businessSlug } = useParams();
  const dispatch = useDispatch();

  const fetchMemberData = useCallback(
    async (params) => {
      const queryParams = {
        businessSlug: businessSlug,
        perPage: params.per_page,
        ...params,
      };
      return dispatch(
        memberHandler.getMemberPagination(businessSlug, queryParams)
      );
    },
    [dispatch, businessSlug]
  );

  const {
    data: memberData,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    filterParams,
  } = useGlobalTable({
    fetchData: fetchMemberData,
    defaultSort: "created_at",
    defaultOrder: "desc",
    defaultPageSize: 10,
  });

  const handleExport = () => {
    const params = {
      s: filterParams.s,
    };
    dispatch(memberHandler.getMemberExcel(businessSlug, params));
  };

  const columns = createTableColumns([
    {
      field: "code",
      headerName: "เลขที่สมาชิก",
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          to={`/${businessSlug}/customer/edit/${row.id}`}
          style={{ color: "#2DC76D", fontWeight: 500, textDecoration: "none" }}
          target="_blank"
          rel="noopener noreferrer"
        >
          {row.code}
        </Link>
      ),
    },
    {
      field: "memberName",
      headerName: "ชื่อสมาชิก",
      flex: 1,
      width: 200,
      sortable: false,
      renderCell: ({ row }) => {
        const isJuristic =
          row.entity_structure?.entity_type?.code === "JURISTIC";

        if (isJuristic) {
          return <div>{row.company_name_th || ""}</div>;
        } else {
          return (
            <div>
              {row.first_name_th || ""} {row.last_name_th || ""}
            </div>
          );
        }
      },
    },
    {
      field: "phone_number",
      headerName: "เบอร์โทรศัพท์",
      flex: 1,
      width: 140,
      sortable: false,
    },
    {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => <div>{row.email || "-"}</div>,
    },
    {
      field: "memberLevel",
      headerName: "ระดับสมาชิก",
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => <div>-</div>,
    },
    {
      field: "totalPoint",
      headerName: "คะแนนสะสม",
      flex: 1,
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="text-[#2DC76D] font-medium">
          {row.totalPoint || row.point || "0"}
        </div>
      ),
    },
    {
      field: "connections",
      headerName: "การเชื่อมต่อ",
      width: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="d-flex align-items-center justify-content-center">
          {row.facebook && (
            <Link
              className="btn me-2 icon-facebook"
              to={row.facebook}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}
          {row.line_uid && (
            <Link
              className="btn me-2 icon-line"
              to={`https://line.me/ti/p/~${row.line_uid}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}
          {!row.facebook && !row.line_uid && <span>-</span>}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "จัดการ",
      width: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          className="btn btn-edit-st-1 me-2"
          to={`/${businessSlug}/customer/edit/${row.id}`}
          title="แก้ไข"
        />
      ),
    },
  ]);

  return (
    <div>
      <nav aria-label="breadcrumb" className="mt-2">
        <ol className="breadcrumb fs-16 ps-0">
          <li className="breadcrumb-item">
            <a href="#" className="tc-7 text-decoration-none">
              สมาชิก/ลูกค้า
            </a>
          </li>
          <li className="breadcrumb-item active tc-1" aria-current="page">
            รายชื่อลูกค้า
          </li>
        </ol>
      </nav>

      <Card className="!rounded-lg mb-4">
        <div className="py-4">
          <div
            className="fs-18 fw-bold me-auto px-4 mb-4"
            style={{ color: "#2DC76D" }}
          >
            รายชื่อลูกค้า/สมาชิก
          </div>

          <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap">
            <div className="d-flex align-items-center mb-2">
              <button className="btn btn-export-st-1" onClick={handleExport}>
                Export
              </button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <TextField
                {...TEXTFIELD_STYLES}
                className="w-[250px]"
                placeholder="ค้นหาชื่อ, รหัสสมาชิก, เบอร์โทร"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
          </div>

          <AllderTable
            data={memberData}
            loading={loading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </Card>
    </div>
  );
}
